/**
 * The clinic's clock: calendar-day and time-of-day maths in the effective
 * clinic timezone, with the zone already bound.
 *
 * Three kinds of value, three shapes:
 *  - a moment: an API timestamp string (or a `Date` built for a request body)
 *  - a calendar day: a `CalendarDay`, `YYYY-MM-DD` — it names a day and has no
 *    zone, so it never needs converting
 *  - the clinic's clock: this module
 *
 * Maths on moments goes through here; text for display goes through
 * `formatDate` / `formatTime` in `@/lib/format-data`. Components use
 * `useClinicClock()` so a zone change re-renders them; API files and plain
 * utilities call `clinicClock()`.
 */
import { getTimezone } from "@/lib/timezone";
import {
  isSameZonedDay,
  zonedDateKey,
  zonedDayStart,
  zonedInstantOnDay,
  zonedWallMinutes,
} from "@/lib/timezone-dates";

export type CalendarDay = `${number}-${number}-${number}`;

const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDay(value: unknown): value is CalendarDay {
  return typeof value === "string" && CALENDAR_DAY_PATTERN.test(value);
}

/**
 * `day` as a date picker's value: a local-midnight `Date` whose local fields
 * are the day. The inverse of `pickedDay`.
 */
export function dayMarker(day: CalendarDay): Date {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date);
}

/**
 * The day a person picked in a date picker, or the day a calendar column
 * stands for. Both arrive as a `Date` at browser-local midnight, so their
 * browser-local fields are the day. Not for moments — use `dayOf`.
 */
export function pickedDay(date: Date): CalendarDay {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}` as CalendarDay;
}

/** Minutes since midnight as the "HH:MM" a `<input type="time">` takes. */
export function timeInputValue(minutesFromMidnight: number): string {
  const hours = String(Math.floor(minutesFromMidnight / 60)).padStart(2, "0");
  const minutes = String(minutesFromMidnight % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/** Whole calendar days from `from` to `to`: 1 for the next day, -1 the day before. */
export function daysBetween(from: CalendarDay, to: CalendarDay): number {
  const utcMidnight = (day: CalendarDay) => Date.parse(`${day}T00:00:00Z`);
  return Math.round((utcMidnight(to) - utcMidnight(from)) / 86_400_000);
}

/** `day` moved by a whole number of calendar days. */
export function shiftDay(day: CalendarDay, days: number): CalendarDay {
  const marker = dayMarker(day);
  marker.setDate(marker.getDate() + days);
  return pickedDay(marker);
}

export interface ClinicClock {
  timeZone: string;
  /** Today in the clinic. */
  today(): CalendarDay;
  /**
   * The clinic day a moment falls on. A value that is already a calendar day
   * passes through unchanged.
   */
  dayOf(value: string | Date): CalendarDay;
  /** Minutes from clinic midnight, 0..1439. */
  wallMinutes(moment: string | Date): number;
  /** The moment `minutesFromMidnight` clinic time on `day`. DST-safe. */
  at(day: CalendarDay, minutesFromMidnight: number): Date;
  /**
   * `dayCount` days from `day` in the clinic as a half-open range for queries:
   * `start` is clinic midnight on `day`, `end` the clinic midnight after the
   * last day (exclusive). Each midnight is resolved separately, so a span over
   * a DST change is 23 or 25 hours short or long, never off by an hour.
   */
  dayRange(day: CalendarDay, dayCount?: number): { start: Date; end: Date };
  /** Whether two moments fall on the same clinic day. */
  isSameDay(a: string | Date, b: string | Date): boolean;
  /** Today as a date picker's marker (local midnight). */
  todayMarker(): Date;
  /**
   * A range of picked days (date-picker markers, `to` inclusive) as clinic
   * bounds: `end` is exclusive, `lastMoment` the millisecond before it for
   * APIs that take an inclusive end. `null` without a start day.
   */
  rangeBounds(
    range: { from?: Date; to?: Date } | undefined,
  ): { start: Date; end: Date; lastMoment: Date } | null;
}

export function clinicClockFor(timeZone: string): ClinicClock {
  return {
    timeZone,
    today: () => zonedDateKey(new Date(), timeZone) as CalendarDay,
    dayOf: (value) =>
      isCalendarDay(value)
        ? value
        : (zonedDateKey(value, timeZone) as CalendarDay),
    wallMinutes: (moment) => zonedWallMinutes(moment, timeZone),
    at: (day, minutesFromMidnight) =>
      zonedInstantOnDay(dayMarker(day), minutesFromMidnight, timeZone),
    dayRange: (day, dayCount = 1) => ({
      start: zonedDayStart(dayMarker(day), timeZone),
      end: zonedDayStart(dayMarker(shiftDay(day, dayCount)), timeZone),
    }),
    isSameDay: (a, b) => isSameZonedDay(a, b, timeZone),
    todayMarker() {
      return dayMarker(this.today());
    },
    rangeBounds(range) {
      if (!range?.from) return null;
      const first = pickedDay(range.from);
      const last = range.to ? pickedDay(range.to) : first;
      const { start, end } = this.dayRange(first, daysBetween(first, last) + 1);
      return { start, end, lastMoment: new Date(end.getTime() - 1) };
    },
  };
}

/**
 * Today in the clinic as a calendar-day marker (local midnight), for date
 * pickers and the date arithmetic they do in marker space. Where no clinic
 * zone is set — the patient portal — this is the browser's today.
 */
export function clinicTodayMarker(): Date {
  return clinicClock().todayMarker();
}

/**
 * A day-level field stored as a moment (a plan's start),
 * as the date picker's marker: the clinic day the moment falls on. Components
 * pass `useClinicClock()`, so the field follows the zone once it settles.
 */
export function clinicDayMarker(
  moment: string | Date,
  clock: ClinicClock,
): Date {
  return dayMarker(clock.dayOf(moment));
}

/** The moment a picked day starts in the clinic, for storing a day-level field. */
export function clinicDayStart(picked: Date, clock: ClinicClock): Date {
  return clock.dayRange(pickedDay(picked)).start;
}

/** Whole clinic days from `moment`'s day to today: 0 today, 1 yesterday. */
export function clinicDaysAgo(
  moment: string | Date,
  clock: ClinicClock,
): number {
  return daysBetween(clock.dayOf(moment), clock.today());
}

/** The clinic clock for non-component code. Components use `useClinicClock`. */
export function clinicClock(): ClinicClock {
  return clinicClockFor(getTimezone());
}

/**
 * Widens a clinic-day window to take in floating all-day entries. Google
 * all-day events are stored as UTC midnight of their date with start = end,
 * and the API keeps only events that end after the window starts, so west of
 * UTC a clinic-midnight window starts hours after the entry for its own first
 * day and drops it before the all-day row, which reads the UTC date, can
 * place it. Timed events in the widened slice still fall outside each view's
 * own clinic-day filter.
 */
export function withFloatingAllDayDates(
  range: { start: Date; end: Date },
  timeZone: string,
): { start: Date; end: Date } {
  const clock = clinicClockFor(timeZone);
  const utcMidnight = (day: CalendarDay) => Date.parse(`${day}T00:00:00Z`);
  const firstDay = clock.dayOf(range.start);
  const dayAfterLast = shiftDay(
    clock.dayOf(new Date(range.end.getTime() - 1)),
    1,
  );
  return {
    start: new Date(Math.min(range.start.getTime(), utcMidnight(firstDay) - 1)),
    end: new Date(Math.max(range.end.getTime(), utcMidnight(dayAfterLast))),
  };
}
